import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  createMissionManagerQuery,
  editMissionManagerQuery,
  getMissionManagerByIdQuery,
  getMissionManagerByMissionIdQuery,
  deleteMissionManagerQuery,
} from "./queries";
import { useUserContext } from "api/user/UserProvider";
import { QUERY_STATUS } from "constants/constants";
import { extractApiFieldErrorsFlat } from "utils/apiError";

const MissionManagerContext = createContext({});

export const useMissionManagerContext = () => useContext(MissionManagerContext);

export const MissionManagerProvider = ({ ...props }) => {
  const { userScope, setApiErrorResponse } = useUserContext();

  const handleApiError = (error) => {
    setApiErrorResponse(
      extractApiFieldErrorsFlat(error?.response?.data?.["bad-request"])
    );
  };
  const [missionManagerStatus, setMissionManagerStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [missionManagerByMissionId, setMissionManagerByMissionId] = useState(
    []
  );
  const [missionManagerById, setMissionManagerById] = useState([]);

  const createMissionManager = useCallback(async (data) => {
    try {
      setMissionManagerStatus(QUERY_STATUS.LOADING);
      const result = await createMissionManagerQuery(data);
      setMissionManagerStatus(QUERY_STATUS.SUCCESS);
      setMissionManagerByMissionId((old) => [...old, result]);
    } catch (error) {
      setMissionManagerStatus(QUERY_STATUS.ERROR);
      handleApiError(error);
      console.error(error);
      throw error;
    }
  }, []);

  const getMissionManagerByMissionId = useCallback(
    async ({ id }) => {
      try {
        setMissionManagerByMissionId([]);
        const result = await getMissionManagerByMissionIdQuery({
          id,
          userScope,
        });
        setMissionManagerByMissionId(result);
      } catch (error) {
        console.error(error);
      }
    },
    [userScope]
  );

  const getMissionManagerById = useCallback(async ({ id }) => {
    try {
      setMissionManagerById(await getMissionManagerByIdQuery({ id }));
    } catch (error) {
      console.error(error);
    }
  }, []);

  const editMissionManager = useCallback(
    async ({ id, data }) => {
      try {
        setMissionManagerStatus(QUERY_STATUS.LOADING);
        const result = await editMissionManagerQuery({ id, data, userScope });
        setMissionManagerByMissionId((old) =>
          old.map((elm) => (elm.id === result.id ? result : elm))
        );
        setMissionManagerStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setMissionManagerStatus(QUERY_STATUS.ERROR);
        handleApiError(error);
        console.error(error);
        throw error;
      }
    },
    [userScope]
  );

  const deleteMissionManager = useCallback(async (id) => {
    try {
      setMissionManagerStatus(QUERY_STATUS.LOADING);
      await deleteMissionManagerQuery(id);
      setMissionManagerStatus(QUERY_STATUS.SUCCESS);
      setMissionManagerByMissionId((old) => old.filter((elm) => elm.id !== id));
    } catch (error) {
      setMissionManagerStatus(QUERY_STATUS.ERROR);
      handleApiError(error);
      console.error(error);
      throw error;
    }
  }, []);

  const values = useMemo(
    () => ({
      createMissionManager,
      missionManagerByMissionId,
      getMissionManagerByMissionId,
      getMissionManagerById,
      missionManagerById,
      editMissionManager,
      deleteMissionManager,
      missionManagerStatus,
      setMissionManagerStatus,
    }),
    [
      createMissionManager,
      missionManagerByMissionId,
      getMissionManagerByMissionId,
      getMissionManagerById,
      missionManagerById,
      editMissionManager,
      deleteMissionManager,
      missionManagerStatus,
      setMissionManagerStatus,
    ]
  );
  return <MissionManagerContext.Provider value={values} {...props} />;
};
